/**
 * The **engine-logic sidecar pack** format + the deterministic exporter + the overlay-layer
 * load/validate path.
 *
 * A sidecar is the distributable output of the authoring factory: it does NOT replace an
 * LCP, it **pairs with and enriches** one. Where an LCP ships the *content* (a weapon's
 * stats, a status's prose), the matching sidecar ships the **executable engine logic** for
 * that content — the {@link OverlayEntry} atoms the effects engine runs. The app loads
 * `standard LCP + matching sidecar` and merges both at load, exactly as it already merges
 * multiple LCPs into one {@link import("@lancer-vtt/compendium").Compendium}.
 *
 * **On-disk shape (a directory):**
 * ```
 * <sidecar>/
 *   manifest.json          — the {@link SidecarManifest} (what LCP it enriches + upstream deps)
 *   overlay/
 *     <slug>.json          — one {@link OverlayEntry} per file
 * ```
 * **File-per-entity is the parallel-safety primitive:** each authored entity is its own
 * file, so N stateless author agents writing N entities produce N disjoint files with zero
 * merge conflicts. The on-disk *filename* is a deterministic slug of the entity id (for
 * git-merge-friendliness); the file *content* carries the canonical `id`, which is what the
 * loader keys on — filenames are never load-bearing.
 *
 * **This module is pure (no `fs`).** It works over an in-memory {@link SidecarFiles} map
 * (relative path → byte content). Disk read/write lives in the CLI layer (`cli/src/sidecar.ts`),
 * mirroring how pack *parsing* lives in `@lancer-vtt/compendium` while pack *loading-from-disk*
 * lives in `cli/src/roster.ts`. Keeping `fs` out matters: `@lancer-vtt/rules` is imported by the
 * frontend bundle.
 *
 * **Deterministic by construction:** {@link exportSidecar} sorts entries by
 * id, serializes every value through {@link canonicalJson} (recursively sorted object keys,
 * stable indentation, trailing newline), and lists ids sorted in the manifest. Re-exporting an
 * unchanged input corpus is therefore **byte-identical** — the property the factory's
 * idempotent re-runs and clean diffs depend on.
 *
 * **The missing-content gate:** {@link validateSidecarContent} reuses the compendium's own
 * missing-content detection at the overlay layer — a sidecar entry whose id no loaded pack
 * provides (and that is not an engine-intrinsic id — a status / generic action / talent-rank / frame-trait)
 * fails LOUDLY ({@link MissingOverlayContentError}), rather than silently enriching a
 * nonexistent entity.
 */

import type { Compendium } from "@lancer-vtt/compendium";
import { ITEM_TYPES } from "@lancer-vtt/compendium";
import type { ZodError, ZodIssue } from "zod";
import type { OverlayEntry } from "./atoms";
import { loadOverlay, type LoadedOverlay } from "./loader";
import { parseOverlayEntry } from "./schema";
import { frameEffectEntries } from "../spawn";

// ── format constants ─────────────────────────────────────────────────────────────────

/** The on-disk sidecar format version (bumped only on a breaking layout change). */
export const SIDECAR_FORMAT_VERSION = 1;

/**
 * The overlay-entry / atom **schema** version a sidecar was authored against (an
 * idempotency-hash input — a bump re-authors every entry). Distinct from the encounter
 * state schema version; this tracks the {@link OverlayEntry}/atom shape. `1` today.
 */
export const OVERLAY_SCHEMA_VERSION = 1;

/**
 * The atom **vocabulary** version a sidecar was authored against (the other hash input
 * — a bump means new atoms/steps shipped, so the co-evolution loop re-runs affected entries).
 * `1` today.
 */
export const ATOM_VOCAB_VERSION = 1;

/** The manifest filename at the sidecar root. */
export const MANIFEST_FILE = "manifest.json";
/** The directory (under the sidecar root) holding the per-entity overlay files. */
export const OVERLAY_DIR = "overlay";

/** Generic combat-action overlay entries (`act_invade`, `act_ram`, …) are engine-intrinsic, not LCP content. */
export const INTRINSIC_ACTION_PREFIX = "act_";

/**
 * The engine's intrinsic **status / condition** registry (Lancer's fixed set). A status
 * overlay entry (`"Impaired"`, `"Lock On"`, …) is keyed by name and is engine-intrinsic — it
 * is NOT shipped by an LCP, so the missing-content gate must recognize it as present rather
 * than flag it. Matched case-/whitespace-insensitively. Extend this when the rules add a
 * status; it is the canonical name set the sidecar validator treats as always-resolvable.
 */
export const INTRINSIC_STATUS_NAMES: readonly string[] = [
  // Conditions
  "Immobilized",
  "Impaired",
  "Jammed",
  "Lock On",
  "Shredded",
  "Slowed",
  "Stunned",
  "Bolster",
  "Exposed",
  // Statuses
  "Danger Zone",
  "Down and Out",
  "Engaged",
  "Hidden",
  "Invisible",
  "Intangible",
  "Prone",
  "Shut Down",
  "Biological",
  // Braced/Flying/Grappled stay intrinsic: each is
  // the bespoke status payload of an intrinsic planter (act_brace, generic
  // apply_status, act_grapple), not a migrated CONTENT entry, so the missing-content
  // gate must recognize them as always-resolvable exactly like the rest of this list.
  "Braced",
  "Flying",
  "Grappled",
];

const INTRINSIC_STATUS_SET = new Set(INTRINSIC_STATUS_NAMES.map((n) => n.trim().toLowerCase()));

// ── the manifest ───────────────────────────────────────────────────────────────────

/**
 * A sidecar pack manifest. Declares the LCP it enriches and the upstream sidecars it
 * layers over (the three-tier chain), plus the schema/vocab versions it was authored against
 * (the idempotency-hash inputs) and the entity-id index (sorted, for integrity + a cheap "what does this
 * pack provide" read without scanning the overlay dir).
 */
export type SidecarManifest = {
  /** The on-disk format version ({@link SIDECAR_FORMAT_VERSION}). */
  formatVersion: number;
  /** This sidecar pack's own stable id (the engine-logic pack id). */
  id: string;
  /** Optional human label. */
  name?: string;
  /** The id of the LCP / content pack this sidecar pairs with and enriches (the target). */
  enriches: string;
  /**
   * Upstream sidecar ids this one layers over, in **layer order** (a base sidecar first). An
   * expansion `depends-on`s core so its authors reference Lock On rather than reimplement it
   * `[]` for a base sidecar. This module declares the field and merges an ordered chain; it
   * does not resolve deps from a registry.
   */
  dependsOn: string[];
  /** The overlay/atom schema version authored against ({@link OVERLAY_SCHEMA_VERSION}). */
  schemaVersion: number;
  /** The atom-vocabulary version authored against ({@link ATOM_VOCAB_VERSION}). */
  atomVocabVersion: number;
  /** The entity ids this sidecar provides, **sorted** (the index + an integrity cross-check). */
  entries: string[];
};

/** The in-memory representation of a sidecar pack: relative path → byte content (canonical JSON). */
export type SidecarFiles = Record<string, string>;

/**
 * One overlay entry that failed the per-entry schema gate — collected rather than thrown, so one bad entry degrades to "this one entry
 * is missing" instead of dropping the whole pack.
 */
export type SidecarEntryRejection = {
  /**
   * The entry's id, recovered from the raw file even though the entry itself failed schema
   * validation. Always present: the structural shape check (`typeof raw.id === "string"`) runs
   * BEFORE the schema gate and still throws (pack-level) if `id` isn't even a string, so by the
   * time an entry can land here its `id` is guaranteed to be *some* string — even one that would
   * itself fail a stricter schema rule (e.g. `""`, which fails `OverlayEntrySchema`'s
   * `z.string().min(1)`). There is no better identifier to report, so the raw value is used as-is.
   */
  id: string;
  /** Human-readable reason, naming the offending field (derived from the zod error). */
  reason: string;
};

/** A parsed sidecar: its manifest + the overlay entries (sorted by id), ready for {@link loadOverlay}. */
export type ParsedSidecar = {
  manifest: SidecarManifest;
  /** The overlay entries that passed the schema gate, sorted by id (deterministic). */
  entries: OverlayEntry[];
  /**
   * Overlay entries that FAILED the schema gate ({@link OverlayEntrySchema} via
   * {@link parseOverlayEntry}), sorted by id. Per-entry and additive — a rejected entry is simply absent from {@link entries}; it does
   * NOT abort the parse or drop its siblings. Pack-level structural failures (missing manifest,
   * a stray file, a manifest/overlay id mismatch, a format-version mismatch) are NOT per-entry
   * facts and still throw — see {@link parseSidecar}'s doc.
   */
  rejected: SidecarEntryRejection[];
};

/**
 * Resolve a (possibly nested) zod error down to the most specific real issue(s) and format them
 * as `"<path>: <message>"`, joined. `EffectEntrySchema`/`EffectAtomSchema` are `z.union`/
 * discriminated-union trees, so a real authoring mistake typically surfaces as an `invalid_union`
 * whose `unionErrors` include one spurious branch per non-matching arm (e.g. every arm's `kind`
 * literal mismatch) alongside the one arm that actually matches the entry's own `kind` — that
 * matching arm's issues are the real complaint (fewest issues, no `kind` mismatch noise), so this
 * recurses into the SMALLEST branch at each `invalid_union` rather than concatenating every arm's
 * noise. Not sidecar-specific — reused as-is wherever an entry is gated through
 * {@link parseOverlayEntry}.
 */
export function describeZodError(error: ZodError): string {
  const resolve = (issues: readonly ZodIssue[]): ZodIssue[] => {
    const out: ZodIssue[] = [];
    for (const issue of issues) {
      if (issue.code === "invalid_union") {
        const branches = issue.unionErrors.map((e) => e.issues);
        const best = branches.reduce((a, b) => (b.length < a.length ? b : a));
        out.push(...resolve(best));
      } else {
        out.push(issue);
      }
    }
    return out;
  };
  return resolve(error.issues)
    .map((i) => `${i.path.join(".")}: ${i.message}`)
    .join("; ");
}

/** Caller-supplied metadata for an export (the parts not derived from the entries). */
export type SidecarExportMeta = {
  /** This sidecar's own id. */
  id: string;
  /** The LCP id it enriches. */
  enriches: string;
  name?: string;
  /** Upstream sidecar ids, in layer order. Omitted → `[]` (a base sidecar). */
  dependsOn?: string[];
  /** Override the authored-against schema version. Omitted → {@link OVERLAY_SCHEMA_VERSION}. */
  schemaVersion?: number;
  /** Override the authored-against vocab version. Omitted → {@link ATOM_VOCAB_VERSION}. */
  atomVocabVersion?: number;
};

// ── canonical (deterministic) serialization ──────────────────────────────────────────

/**
 * Recursively sort object keys (arrays keep their order — order is semantic for `effects[]`),
 * yielding a value whose `JSON.stringify` is stable regardless of the input's key insertion
 * order. The load-bearing half of the byte-identical-re-export invariant.
 */
function canonicalize(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(canonicalize);
  if (value && typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(value as Record<string, unknown>).sort()) {
      out[key] = canonicalize((value as Record<string, unknown>)[key]);
    }
    return out;
  }
  return value;
}

/**
 * Serialize a value to **canonical JSON**: recursively sorted object keys, 2-space indent, and
 * a trailing newline (so a sidecar file is a well-formed text file + a clean git diff). Pure +
 * deterministic — the same value always yields the same bytes.
 */
export function canonicalJson(value: unknown): string {
  return JSON.stringify(canonicalize(value), null, 2) + "\n";
}

/**
 * Map an entity id to a deterministic, filesystem-safe filename **stem** (no extension): lower-
 * cased, every run of non-`[a-z0-9]` characters collapsed to `-`, trimmed. `"Lock On"` →
 * `"lock-on"`, `"t_combat_reflexes:1"` → `"t_combat_reflexes-1"`. The file content's `id` stays
 * canonical; the slug only names the file. Two distinct ids *could* collide to one slug — the
 * exporter detects that and throws (it never silently overwrites).
 */
export function slugifyEntityId(id: string): string {
  const slug = id
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "-")
    .replace(/^-+|-+$/g, "");
  if (slug === "") throw new Error(`cannot derive a filename for overlay entry id "${id}" (empty slug)`);
  return slug;
}

// ── export (entries → deterministic sidecar files) ───────────────────────────────────

/**
 * Produce the deterministic {@link SidecarFiles} for a sidecar pack from authored overlay
 * entries + manifest metadata. Entries are sorted by id; each becomes
 * `overlay/<slug>.json` (canonical JSON); the manifest lists the sorted ids. Re-exporting an
 * unchanged corpus is **byte-identical**. Throws on a duplicate entry id or a slug collision
 * (never silently drops/overwrites an entity).
 */
export function exportSidecar(entries: readonly OverlayEntry[], meta: SidecarExportMeta): SidecarFiles {
  const sorted = [...entries].sort((a, b) => a.id.localeCompare(b.id));

  // Reject duplicate ids up front (file-per-entity assumes one entry per id).
  const seenIds = new Set<string>();
  for (const e of sorted) {
    if (seenIds.has(e.id)) throw new Error(`duplicate overlay entry id "${e.id}" — file-per-entity requires unique ids`);
    seenIds.add(e.id);
  }

  const files: SidecarFiles = {};
  const slugToId = new Map<string, string>();
  for (const entry of sorted) {
    const slug = slugifyEntityId(entry.id);
    const prior = slugToId.get(slug);
    if (prior !== undefined && prior !== entry.id) {
      throw new Error(`overlay entry ids "${prior}" and "${entry.id}" both slug to "${slug}.json" — rename one`);
    }
    slugToId.set(slug, entry.id);
    files[`${OVERLAY_DIR}/${slug}.json`] = canonicalJson(entry);
  }

  const manifest: SidecarManifest = {
    formatVersion: SIDECAR_FORMAT_VERSION,
    id: meta.id,
    ...(meta.name !== undefined ? { name: meta.name } : {}),
    enriches: meta.enriches,
    dependsOn: meta.dependsOn ?? [],
    schemaVersion: meta.schemaVersion ?? OVERLAY_SCHEMA_VERSION,
    atomVocabVersion: meta.atomVocabVersion ?? ATOM_VOCAB_VERSION,
    entries: sorted.map((e) => e.id),
  };
  files[MANIFEST_FILE] = canonicalJson(manifest);

  return files;
}

// ── parse (sidecar files → manifest + entries) ───────────────────────────────────────

/**
 * Parse {@link SidecarFiles} back into a {@link ParsedSidecar}. Reads the manifest, then runs
 * every `overlay/*.json` entry through the FULL {@link parseOverlayEntry} schema gate
 * — this is the boot rail, so a schema-invalid entry that slipped past it would load silently
 * and misbehave at runtime rather than being rejected.
 *
 * Two different failure classes, two different behaviors — deliberately asymmetric, do not
 * unify them:
 * - **Per-entry schema failures collect into {@link ParsedSidecar.rejected}, they do NOT throw.**
 *   One bad entry must not drop the other N-1 — the whole-sidecar skip pattern used elsewhere
 *   in this codebase (the `.lcp` pack loop) is deliberately NOT reused here. `rooms.ts`'s
 *   sidecar loop now agrees on both per-entry buckets: `decisions/0135`'s 2026-09-03 amendment
 *   gave content misses the same treatment via {@link partitionSidecarContent}.
 * - **Pack-level structural failures still throw**: a missing manifest, a stray/unexpected file,
 *   a manifest/overlay id mismatch, or a format-version mismatch. These aren't per-entry facts
 *   and have no sane partial recovery. The `typeof raw.id === "string" && Array.isArray(raw.effects)`
 *   shape check also stays a throw — it's what lets a rejected entry's id be recovered at all
 *   (see {@link SidecarEntryRejection.id}'s doc).
 *
 * **The manifest integrity cross-check runs against every file's id, REJECTED ones included —
 * not just the schema-accepted `entries`.** A rejected entry is still a real file the manifest
 * legitimately declares; if the cross-check only saw `entries`, a single rejected entry would
 * make the manifest look like it "declares an id no file provides," throwing the exact
 * whole-pack failure this design forbids. Only the *schema* verdict decides `entries` vs `rejected` — the
 * *integrity* check doesn't care which bucket an id landed in.
 *
 * `entries` and `rejected` are each returned sorted by id (deterministic), so
 * `parseSidecar(exportSidecar(xs)).entries` round-trips exactly as before.
 */
export function parseSidecar(files: SidecarFiles): ParsedSidecar {
  const manifestRaw = files[MANIFEST_FILE];
  if (manifestRaw === undefined) throw new Error(`sidecar is missing ${MANIFEST_FILE}`);
  const manifest = JSON.parse(manifestRaw) as SidecarManifest;
  if (manifest.formatVersion !== SIDECAR_FORMAT_VERSION) {
    throw new Error(`sidecar format version ${manifest.formatVersion} != supported ${SIDECAR_FORMAT_VERSION}`);
  }

  const entries: OverlayEntry[] = [];
  const rejected: SidecarEntryRejection[] = [];
  const fileIds = new Set<string>();
  const prefix = `${OVERLAY_DIR}/`;
  for (const path of Object.keys(files)) {
    if (path === MANIFEST_FILE) continue;
    if (!path.startsWith(prefix) || !path.endsWith(".json")) {
      throw new Error(`unexpected file in sidecar: "${path}" (only ${MANIFEST_FILE} + ${prefix}*.json allowed)`);
    }
    const raw = JSON.parse(files[path]!) as OverlayEntry;
    if (typeof raw.id !== "string" || !Array.isArray(raw.effects)) {
      throw new Error(`malformed overlay entry in "${path}" (needs { id: string, effects: [] })`);
    }
    // Recorded regardless of the schema verdict below — the integrity cross-check must see a
    // rejected entry's id too (the trap: it must NOT look like a file-less manifest entry).
    fileIds.add(raw.id);

    const gate = parseOverlayEntry(raw);
    if (gate.success) {
      entries.push(gate.data);
    } else {
      rejected.push({ id: raw.id, reason: describeZodError(gate.error) });
    }
  }

  // Integrity: the overlay files' ids (accepted AND rejected) must exactly match the manifest's
  // declared set. Deliberately NOT derived from `entries` alone — see the doc above.
  const manifestIds = new Set(manifest.entries);
  for (const id of manifestIds) {
    if (!fileIds.has(id)) throw new Error(`manifest declares entry "${id}" but no overlay/ file provides it`);
  }
  for (const id of fileIds) {
    if (!manifestIds.has(id)) throw new Error(`overlay/ file provides entry "${id}" not declared in the manifest`);
  }

  entries.sort((a, b) => a.id.localeCompare(b.id));
  rejected.sort((a, b) => a.id.localeCompare(b.id));
  return { manifest, entries, rejected };
}

// ── the overlay-layer missing-content gate ───────────────────────────────────────────

/** One overlay entry whose id resolves to nothing the engine knows about (the loud gate's payload). */
export type MissingOverlayRef = {
  /** The unresolved overlay entry id. */
  id: string;
  /** Why it didn't resolve (the human-facing reason). */
  reason: string;
};

/**
 * Thrown by {@link requireSidecarContent} when a sidecar references entity ids that no loaded
 * pack provides and that are not engine-intrinsic. The overlay-layer analogue of
 * {@link import("@lancer-vtt/compendium").MissingContentError} — it names every offending
 * id so the failure is loud + actionable ("install the LCP that ships these"), never a silent
 * enrichment of a nonexistent entity. A miss here is always an **item-shaped** id (`mw_…`/`cb_…`/…
 * with no backing compendium item) or a talent-rank id whose base talent isn't loaded — a bare
 * display-name **content status** (e.g. `"Argonaut Shield"`) never lands here, since the pack
 * that introduces it IS its definition (see {@link validateSidecarContent}'s content-status
 * branch).
 */
export class MissingOverlayContentError extends Error {
  readonly missing: MissingOverlayRef[];
  constructor(missing: MissingOverlayRef[]) {
    const list = missing.map((m) => `  - "${m.id}": ${m.reason}`).join("\n");
    super(
      `Sidecar references ${missing.length} entity id(s) no loaded pack provides:\n${list}\n` +
        `Install the LCP this sidecar enriches, or remove/fix the offending overlay entries.`,
    );
    this.name = "MissingOverlayContentError";
    this.missing = missing;
  }
}

/** Whether an id is an engine-intrinsic status name (case-/whitespace-insensitive). */
function isIntrinsicStatus(id: string): boolean {
  return INTRINSIC_STATUS_SET.has(id.trim().toLowerCase());
}

/** Whether an id is an engine-intrinsic generic action (`act_…`). */
function isIntrinsicAction(id: string): boolean {
  return id.startsWith(INTRINSIC_ACTION_PREFIX);
}

/** Whether an id resolves to a compendium item of any type. */
function resolvesAsItem(compendium: Compendium, id: string): boolean {
  return ITEM_TYPES.some((t) => compendium.has(t, id));
}

/**
 * A pilot talent-rank overlay entry is keyed `${talentId}:${rank}` (pilot-combat-effects). It
 * resolves iff the base talent id is in the compendium. Returns the base id when the shape
 * matches, else `undefined`.
 */
function talentRankBase(id: string): string | undefined {
  const m = /^(.+):(\d+)$/.exec(id);
  return m ? m[1] : undefined;
}

/**
 * Whether an id is a **frame-trait or core-system** overlay entry
 * (`everest_initiative`/`everest_replaceable_parts`, or a core-system id such as
 * `everest_hyperspec_fuel_injector`/
 * `everest_hyperspec_fuel_injector_boost`). Unlike a talent-rank id (`${talentId}:${rank}`, a
 * reversible split on `:`), a frame-trait/core-system id is a `slugifySnake(...)`-joined
 * composite (`spawn.ts`'s `frameEffectEntries` convention) — an IRREVERSIBLE join (each part is
 * independently slugified and concatenated with `_`, and any part may itself contain
 * spaces/punctuation that collapse to `_` too), so there is no base-id extraction to check
 * against the compendium the way `talentRankBase` does. Instead this scans every loaded frame's
 * own trait + core-system effect ids ({@link frameEffectEntries} — the SAME derivation the
 * freeze itself uses, so this can never accept an id the freeze wouldn't also produce) for a
 * match. `id` is item-shaped (matches `ITEM_SHAPED_ID`, e.g. `everest_initiative`), so without
 * this check it would otherwise fall through to the loud "no loaded pack provides this id"
 * miss — exactly the gap that made a booted world SKIP the whole core-rulebook sidecar the
 * first time the frame-trait entries shipped (caught by `sidecarRuntimeLoading.integration.test.ts`).
 * That whole-sidecar blast radius is history (`decisions/0135`'s 2026-09-03 amendment made a
 * content miss per-entry), but this check is not: without it every frame-trait entry would still
 * be skipped individually and silently absent from play.
 */
function resolvesAsFrameTrait(compendium: Compendium, id: string): boolean {
  for (const frame of compendium.list("frame")) {
    if (frameEffectEntries(frame as unknown as Record<string, unknown>).some((e) => e.id === id)) return true;
  }
  return false;
}

/** A compendium-item-shaped id: lowercase, alphanumeric, and a `snake_case`-style type prefix (`mw_`, `ms_`, `cb_`, `pg_`, `tg_`, `frame_`, …). */
const ITEM_SHAPED_ID = /^[a-z][a-z0-9]*_/;

/**
 * Whether an id is a **content status** — a bare display-name status marker (`"Argonaut
 * Shield"`, `"Bondmate"`, `"Leadership Die"`, `"Projected Shield"`, `"Stay of Execution"`) that
 * the pack itself *introduces* rather than references. Statuses are an open namespace: content
 * legitimately mints new ones, and a content status has no compendium row to validate against
 * (it's a definition, not a reference) — so it isn't item-shaped, isn't an `act_…` intrinsic
 * action, and isn't a `${talentId}:${rank}` id. This deliberately can't catch a typo'd status
 * name; the gate's real job is dangling *item* enrichments (snake_case ids), which this
 * discriminator leaves untouched.
 */
function isContentStatusName(id: string): boolean {
  return !ITEM_SHAPED_ID.test(id) && !isIntrinsicAction(id) && talentRankBase(id) === undefined;
}

/**
 * Classify each overlay entry id against the loaded compendium + the engine's intrinsic
 * namespaces, returning the ids that resolve to **nothing** (the overlay-layer
 * missing-content list). An id resolves when it is: a compendium item (any type); an intrinsic
 * status name; an intrinsic generic action (`act_…`); a `${talentId}:${rank}` whose base talent
 * is in the compendium; or a **content status** — a pack-introduced, non-item-shaped display-name
 * status marker ({@link isContentStatusName}). Anything else is missing (loud) — in practice,
 * only item-shaped ids (`mw_…`/`cb_…`/…) with no compendium-backed item, and talent-rank ids
 * whose base talent isn't loaded, can still miss. Does NOT throw — returns the list so a caller
 * can report every gap at once; {@link requireSidecarContent} is the throwing variant.
 */
export function validateSidecarContent(entries: readonly OverlayEntry[], compendium: Compendium): MissingOverlayRef[] {
  return classifySidecarContent(entries, compendium, () => GENERIC_UNRESOLVABLE_REASON).unresolvable;
}

/**
 * The reason string {@link validateSidecarContent} reports for a plain item-shaped miss — the
 * AUTHORING-time wording, deliberately family-agnostic. `lvtt overlay validate` prints this
 * verbatim and its stdout is a shipped surface, so this string is pinned by that surface, not
 * just by the unit tests: {@link partitionSidecarContent} layers the pack-family hint on top for
 * the BOOT-time skip line, and the two must not converge (see {@link classifySidecarContent}).
 */
const GENERIC_UNRESOLVABLE_REASON = "no loaded pack provides this id, and it is not an engine-intrinsic status/action";

/**
 * The single id-resolution cascade both content gates run — extracted so
 * {@link validateSidecarContent} (authoring, throws via {@link requireSidecarContent}) and
 * {@link partitionSidecarContent} (boot, per-entry skip) cannot drift apart. They were literal
 * duplicates of this loop through Phase 1 of `sidecar-per-entry-unresolvable-skip`; folding them
 * is that project's item 2.0.
 *
 * An id resolves when it is: a compendium item (any type); an intrinsic status name; an intrinsic
 * generic action (`act_…`); a `${talentId}:${rank}` whose base talent is in the compendium; a
 * frame trait ({@link resolvesAsFrameTrait}); or a **content status** — a pack-introduced,
 * non-item-shaped display-name status marker ({@link isContentStatusName}). Anything else is
 * unresolvable — in practice only item-shaped ids (`mw_…`/`cb_…`/…) with no compendium-backed
 * item, and talent-rank ids whose base talent isn't loaded.
 *
 * `missReason` is the ONLY thing the two callers vary, and that is deliberate: the two reason
 * strings serve different readers and **must stay different**. The authoring gate's wording is
 * printed by `lvtt overlay validate`, a shipped surface a naive "just delegate" would have
 * silently rewritten for all nine shipped `npcf_*` ids. The talent-rank reason is identical for
 * both callers, so it stays here rather than being parameterized.
 */
function classifySidecarContent(
  entries: readonly OverlayEntry[],
  compendium: Compendium,
  missReason: (id: string) => string,
): { resolved: OverlayEntry[]; unresolvable: MissingOverlayRef[] } {
  const resolved: OverlayEntry[] = [];
  const unresolvable: MissingOverlayRef[] = [];
  for (const entry of entries) {
    const id = entry.id;
    // NOTE: predicate ORDER is load-bearing and is the order both gates used before the fold —
    // do not "tidy" it into one boolean chain. The talent-rank branch sits BETWEEN the
    // intrinsic checks and the frame-trait/content-status checks, so an id that is both
    // talent-rank-shaped and resolvable as an item/intrinsic resolves on the earlier predicate,
    // and `isContentStatusName` (which itself calls `talentRankBase`) is only ever reached for
    // ids the talent branch already declined.
    if (resolvesAsItem(compendium, id) || isIntrinsicStatus(id) || isIntrinsicAction(id)) {
      resolved.push(entry);
      continue;
    }
    const base = talentRankBase(id);
    if (base !== undefined) {
      if (compendium.has("talent", base)) resolved.push(entry);
      else unresolvable.push({ id, reason: `talent-rank entry, but no loaded pack provides talent "${base}"` });
      continue;
    }
    if (resolvesAsFrameTrait(compendium, id) || isContentStatusName(id)) {
      resolved.push(entry);
      continue;
    }
    unresolvable.push({ id, reason: missReason(id) });
  }
  return { resolved, unresolvable };
}

/**
 * A rough guess at which pack family an unresolvable item-shaped id belongs to, read off its
 * id prefix — used only to make the per-entry skip's boot line and report `reason` actionable
 * ("install the NPC pack") instead of the bare generic miss text. This is a hint, not a
 * guarantee: a prefix absent from the table falls back to the same reason
 * {@link validateSidecarContent} uses. `npcf_` is the one family shipped content actually uses
 * today (`npc-features-fielded-roster`'s purchased NPC `.lcp`); extend the table when another
 * pack family starts keying overlay entries by a recognizable prefix.
 */
const PACK_FAMILY_HINTS: readonly { readonly prefix: string; readonly family: string }[] = [
  { prefix: "npcf_", family: "an NPC content pack (.lcp)" },
];

function unresolvableContentReason(id: string): string {
  const hint = PACK_FAMILY_HINTS.find((h) => id.startsWith(h.prefix));
  if (hint) return `no loaded pack provides this id — it belongs to ${hint.family}`;
  return GENERIC_UNRESOLVABLE_REASON;
}

/**
 * Partition overlay entries into those that resolve against the loaded compendium and those
 * that don't, per {@link decisions/0135}'s per-entry-unresolvable amendment (per entry, not
 * whole-sidecar). Same classification `validateSidecarContent` runs, but returns BOTH buckets —
 * `resolved` entries to keep loading, `unresolvable` refs to skip and report — instead of
 * throwing on the first miss. The loader (`WorldRooms.boot`) is the one caller: a world that
 * lacks a pack a sidecar entry references still boots, minus that one entry, rather than
 * dropping the whole sidecar the way {@link requireSidecarContent} would. Authoring-time
 * validation (`lvtt overlay validate`) stays on the throwing gate — a pack that ships an
 * unresolvable id is a shipping mistake; a world that lacks a pack is a runtime configuration
 * the server must still come up in.
 */
export function partitionSidecarContent(
  entries: readonly OverlayEntry[],
  compendium: Compendium,
): { resolved: OverlayEntry[]; unresolvable: MissingOverlayRef[] } {
  return classifySidecarContent(entries, compendium, unresolvableContentReason);
}

/**
 * Validate overlay entries against the compendium and **throw loudly** on any miss. The
 * gate the app's load path runs so a sidecar can never silently enrich content the user doesn't
 * have installed.
 */
export function requireSidecarContent(entries: readonly OverlayEntry[], compendium: Compendium): void {
  const missing = validateSidecarContent(entries, compendium);
  if (missing.length > 0) throw new MissingOverlayContentError(missing);
}

// ── the app load path (sidecar files + compendium → a validated LoadedOverlay) ───────

/**
 * The end-to-end **app load path**: parse a sidecar, run the missing-content gate
 * against the loaded compendium (throws {@link MissingOverlayContentError} on a miss), and
 * build the {@link LoadedOverlay} the effects engine consumes — exactly the overlay the
 * hand-modeled `SEED_OVERLAY` is, but sourced from a distributable pack. Pass `compendium:
 * undefined` to skip the gate (a round-trip/determinism check that needs no content).
 */
export type LoadSidecarArgs = {
  files: SidecarFiles;
  /** The merged compendium to validate against. Omit to skip validation. */
  compendium?: Compendium;
};

export function loadSidecarOverlay(args: LoadSidecarArgs): { manifest: SidecarManifest; overlay: LoadedOverlay } {
  const { files, compendium } = args;
  const { manifest, entries } = parseSidecar(files);
  if (compendium) requireSidecarContent(entries, compendium);
  return { manifest, overlay: loadOverlay(entries) };
}

/**
 * Merge an ordered **chain** of parsed sidecars into one {@link LoadedOverlay} (the three-tier
 * layering), last-wins on an id collision — the same precedence the {@link
 * import("@lancer-vtt/compendium").Compendium} uses for stacked LCPs. The chain is given in
 * layer order (base first, expansions after). This provides the merge only; resolving a
 * sidecar's `dependsOn` ids to on-disk packs is the scaffolder's job.
 */
export function loadSidecarChain(chain: readonly ParsedSidecar[]): LoadedOverlay {
  const all: OverlayEntry[] = [];
  for (const sc of chain) all.push(...sc.entries);
  return loadOverlay(all);
}
